/**
 * Email-change domain model: the request draft, the pending change, and the
 * submit lifecycle.
 *
 * The draft holds a password in clear, which is unavoidable: it is the bound value
 * of a controlled input. What IS avoidable is letting it outlive the form, so the
 * slice wipes it on close, on success and on unmount (see `email-change.slice.ts`).
 * Do not add a `persist` to this state.
 */

/**
 * The two fields the form edits. Both are required by the endpoint: the password
 * re-authenticates the caller, and the address is what will be confirmed.
 */
export interface EmailChangeDraft {
  currentPassword: string;
  newEmail: string;
}

export const emptyDraft: EmailChangeDraft = {
  currentPassword: '',
  newEmail: '',
};

/** Per-field validation messages, keyed by draft field. Empty = valid. */
export type EmailChangeValidationErrors = Partial<Record<keyof EmailChangeDraft, string>>;

/**
 * A change awaiting confirmation at `pendingEmail`.
 *
 * This is the shape that makes the flow legible: nothing has moved yet, and the
 * account still answers to its current address until the link mailed to
 * `pendingEmail` is followed. Showing it is also the only way someone can notice a
 * request they never made; see the section view.
 */
export interface PendingEmailChange {
  pendingEmail: string;
  /** ISO-8601, as the API sends it. Formatting belongs to the view. */
  expiresAt: string;
}

/** The submit lifecycle. `succeeded` is transient; the UI acknowledges it. */
export type SubmitStatus = 'idle' | 'submitting' | 'succeeded' | 'failed';

/** The pending-change load lifecycle, so the section can stay quiet until it knows. */
export type LoadStatus = 'idle' | 'loading' | 'loaded' | 'failed';
